import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ProLayout } from "@ant-design/pro-components";
import {
  KeyOutlined,
  LogoutOutlined,
  MoonOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  FileTextOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { App as AntdApp, Button, Dropdown, Tooltip, Typography } from "antd";

import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { useIsNarrow } from "@/hooks/useIsNarrow";
import { useNavStore } from "@/stores/navStore";
import { useBreadcrumbStore } from "@/stores/breadcrumbStore";
import { isAdmin } from "@/lib/roles";
import {
  buildMenuRoutes,
  documentTitle,
  flattenMenuRoutes,
  saseMenuPages,
  selectedMenuKey,
} from "@/layouts/menuRoutes";
import { useCapabilities } from "@/lib/capabilities";
import { logout as apiLogout } from "@/features/auth/api";
import AccountBanner from "@/features/billing/AccountBanner";
import ActingForBanner from "@/features/sase/partner/ActingForBanner";
import { useMonthSpend } from "@/features/billing/hooks";
import { formatMoney } from "@/lib/money";
import Breadcrumbs from "@/components/Breadcrumbs";
import DiscardGuard from "@/components/DiscardGuard";
import GlobalSearch, { type SearchScope } from "@/components/GlobalSearch";
import ProductTour from "@/components/ProductTour";
import { useTourForRoute } from "@/hooks/useTourForRoute";
import ActivityPanel from "@/features/operations/ActivityPanel";
import OperationsTracker from "@/features/operations/OperationsTracker";
import { useProductView } from "@/features/sase/hooks";
import EditionGate from "./EditionGate";

const MENU_LABELS: Record<string, string> = Object.fromEntries(
  flattenMenuRoutes().map((r) => [r.path, r.name]),
);

function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const user = useAuthStore((state) => state.user);
  const clearSession = useAuthStore((state) => state.clear);
  const themeMode = useUiStore((state) => state.theme);
  const toggleTheme = useUiStore((state) => state.toggleTheme);
  const recordVisit = useNavStore((state) => state.visit);
  const [searchOpen, setSearchOpen] = useState(false);
  const isNarrow = useIsNarrow();
  // Responsive sidebar: antd's "md" breakpoint (<768px) auto-collapses it so
  // the menu doesn't eat a phone-width viewport; the header trigger still
  // lets the user reopen it. jsdom never renders the ProLayout menu, so this
  // is only verifiable in a real browser (manual pass).
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768,
  );
  // Bumped to replay the current route's tour on demand; null = never asked.
  const [tourRequestedAt, setTourRequestedAt] = useState<number | null>(null);
  const routeTour = useTourForRoute();

  // SASE-30b: the SASE edition has no cloud billing (its API refuses /billing); the
  // spend figure, the credentials link and the account banner are the cloud's.
  const capabilities = useCapabilities();
  const saseEdition = capabilities.sase?.edition === "sase";
  // Month-to-date spend for the account menu (AWS shows estimated charges in the
  // header): the same figure, rounding and format as the Billing page.
  const { data: monthUsage } = useMonthSpend({ enabled: !saseEdition });
  const monthSpend = monthUsage?.spend;

  // Cmd/Ctrl+K opens the global resource search palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Record visits to known sections so the Cmd/K palette can offer recents.
  useEffect(() => {
    const label = MENU_LABELS[location.pathname];
    if (label) recordVisit({ path: location.pathname, label });
  }, [location.pathname, recordVisit]);

  // A per-route document title (browser-tab + screen-reader wayfinding; every
  // page otherwise shares the same generic title). A detail page is titled by
  // its section, and by the resource's name once the page has loaded it.
  const resourceName = useBreadcrumbStore((s) => s.names[location.pathname]);
  useEffect(() => {
    document.title = documentTitle(location.pathname, resourceName);
  }, [location.pathname, resourceName]);

  const autoScalingAvailable = capabilities.compute.auto_scaling;
  const scheduledActionsAvailable = capabilities.compute.scheduled_actions;
  const vpnAvailable = capabilities.network.vpn;
  const aclsAvailable = capabilities.network.acl;
  const flowLogsAvailable = capabilities.network.flow_logs;
  const trafficMirrorsAvailable = capabilities.network.traffic_mirroring;
  // "Offered", not "healthy right now": the entry stays through a delivery
  // node outage (available implies offered; an older backend sends only the first).
  const contentDeliveryAvailable = Boolean(
    capabilities.network.content_delivery_offered || capabilities.network.content_delivery,
  );
  const videoStreamingAvailable = Boolean(capabilities.media?.video_streaming);
  const sharesAvailable = capabilities.storage.shares;
  const backupPlansAvailable = Boolean(
    capabilities.storage.backups && capabilities.compute.scheduled_actions,
  );
  const logsAvailable = capabilities.operations.logs;
  const agentGatewaysAvailable = capabilities.agent_gateway;
  const parameterGroupsAvailable = Boolean(capabilities.database.parameter_groups);
  const privateAccessAvailable = Boolean(capabilities.access?.private_access);
  // SASE-1 (SD-01): the section, and the account's product view — which
  // applies only while the section exists on this cloud.
  const saseAvailable = Boolean(capabilities.sase?.enabled);
  const saseAnalyticsAvailable = Boolean(capabilities.sase?.modules.analytics);
  const saseIncidentsAvailable = Boolean(capabilities.sase?.modules.incidents);
  const saseAiSecurityAvailable = Boolean(capabilities.sase?.modules.ai_security);
  // SASE-30: the standalone edition keeps SASE + Administration's SASE pages
  // (the view is always "SASE only" there, see useProductView); `saseEdition` above.
  const saseDataProtectionAvailable = Boolean(capabilities.sase?.modules.data_protection);
  const saseExperienceAvailable = Boolean(capabilities.sase?.modules.experience);
  const productView = useProductView(saseAvailable);
  // SASE-9/10/11: a module's leaf needs the section AND the module.
  const saseModules = capabilities.sase?.modules;
  const saseDnsProtection = Boolean(saseModules?.dns_protection);
  const saseFirewall = Boolean(saseModules?.firewall);
  const saseThreatProtection = Boolean(saseModules?.threat_protection);
  const menuRoutes = useMemo(
    () =>
      buildMenuRoutes(
        isAdmin(user),
        {
          autoScaling: autoScalingAvailable,
          scheduledActions: scheduledActionsAvailable,
          vpn: vpnAvailable,
          acls: aclsAvailable,
          flowLogs: flowLogsAvailable,
          trafficMirrors: trafficMirrorsAvailable,
          contentDelivery: contentDeliveryAvailable,
          videoStreaming: videoStreamingAvailable,
          shares: sharesAvailable,
          backupPlans: backupPlansAvailable,
          logs: logsAvailable,
          agentGateways: agentGatewaysAvailable,
          parameterGroups: parameterGroupsAvailable,
          privateAccess: privateAccessAvailable,
          sase: saseAvailable,
          saseAnalytics: saseAnalyticsAvailable,
          saseIncidents: saseIncidentsAvailable,
          saseDnsProtection,
          saseFirewall,
          saseThreatProtection,
          saseAiSecurity: saseAiSecurityAvailable,
          saseEdition,
          saseDataProtection: saseDataProtectionAvailable,
          saseExperience: saseExperienceAvailable,
        },
        productView,
      ),
    [
      user,
      autoScalingAvailable,
      scheduledActionsAvailable,
      vpnAvailable,
      aclsAvailable,
      flowLogsAvailable,
      trafficMirrorsAvailable,
      contentDeliveryAvailable,
      videoStreamingAvailable,
      sharesAvailable,
      backupPlansAvailable,
      logsAvailable,
      agentGatewaysAvailable,
      parameterGroupsAvailable,
      privateAccessAvailable,
      saseAvailable,
      saseAnalyticsAvailable,
      saseIncidentsAvailable,
      saseDnsProtection,
      saseFirewall,
      saseThreatProtection,
      saseAiSecurityAvailable,
      saseEdition,
      saseDataProtectionAvailable,
      saseExperienceAvailable,
      productView,
    ],
  );

  // Cmd+K follows the product view (SD-01): in "SASE only" it offers SASE
  // destinations alone (the SASE-18 record's open item).
  const searchScope = useMemo<SearchScope>(() => {
    const sasePages = saseMenuPages(menuRoutes);
    const open = (path: string) => sasePages.some((p) => p.path === path);
    return {
      saseOnly: saseAvailable && productView === "sase_only",
      sasePages,
      saseAgentGateways: open("/sase/ai/agent-gateways"),
      saseAnalytics: open("/sase/analytics"),
    };
  }, [menuRoutes, saseAvailable, productView]);

  // One highlighted leaf: the longest menu path the URL is under (ProLayout
  // would light every prefix, e.g. SASE → Overview on /sase/get-started).
  const selectedKey = useMemo(
    () => selectedMenuKey(menuRoutes, location.pathname),
    [menuRoutes, location.pathname],
  );

  // "SASE only" starts at the SASE overview: the cloud dashboard is not part
  // of that view (a presenter signs in and nothing else is on screen).
  useEffect(() => {
    if (saseAvailable && productView === "sase_only" && location.pathname === "/") {
      navigate("/sase", { replace: true });
    }
  }, [saseAvailable, productView, location.pathname, navigate]);

  const avatarProps = useMemo(
    () => ({
      // A full address overflows a 375px header; the account menu still
      // shows it in full one tap away.
      title: isNarrow ? undefined : (user?.email ?? "Guest"),
      size: "small" as const,
      render: (_: unknown, dom: React.ReactNode) => (
        <Dropdown
          menu={{
            items: [
              {
                // Account context: who you are + which tenant you're scoped to.
                // The portal is single-account per session; this surfaces the
                // active identity (AWS-console-style) and a copyable project id.
                key: "account-info",
                disabled: true,
                label: (
                  <div style={{ lineHeight: 1.5, paddingBlock: 4 }}>
                    <Typography.Text strong>
                      {user?.email ?? "Guest"}
                    </Typography.Text>
                    <div>
                      <Typography.Text type="secondary">
                        Role: {user?.role ?? "—"}
                      </Typography.Text>
                    </div>
                    {user?.openstack_project_id ? (
                      <Typography.Text
                        type="secondary"
                        copyable={{ text: user.openstack_project_id }}
                      >
                        Project: {user.openstack_project_id.slice(0, 8)}…
                      </Typography.Text>
                    ) : null}
                  </div>
                ),
              },
              ...(saseEdition
                ? []
                : [
                    { type: "divider" as const },
                    {
                      key: "spend",
                      label: (
                        <Typography.Text>
                          This month:{" "}
                          <Typography.Text strong>
                            {monthSpend != null
                              ? formatMoney(monthSpend)
                              : "—"}
                          </Typography.Text>
                        </Typography.Text>
                      ),
                      onClick: () => navigate("/billing"),
                    },
                  ]),
              { type: "divider" as const },
              ...(saseEdition
                ? []
                : [
                    {
                      key: "credentials",
                      icon: <KeyOutlined />,
                      label: "API keys & credentials",
                      onClick: () => navigate("/credentials"),
                    },
                  ]),
              {
                key: "account",
                icon: <SettingOutlined />,
                label: "Account settings",
                onClick: () => navigate("/account"),
              },
              {
                key: "licences",
                icon: <FileTextOutlined />,
                label: "Licences",
                onClick: () => navigate("/about/licences"),
              },
              {
                key: "logout",
                icon: <LogoutOutlined />,
                label: "Logout",
                onClick: async () => {
                  try {
                    await apiLogout();
                  } catch {
                    /* best effort */
                  }
                  clearSession();
                  message.success("Signed out");
                  navigate("/login", { replace: true });
                },
              },
            ],
          }}
          // Opened by a click: on hover it dropped over the Save and Create
          // buttons of a drawer below it, Logout first under the pointer (M-15).
          trigger={["click"]}
        >
          {dom}
        </Dropdown>
      ),
    }),
    [user, clearSession, navigate, message, monthSpend, isNarrow, saseEdition],
  );

  return (
    <ProLayout
      title={false}
      logo="/omnitech-logo.png"
      layout="mix"
      fixSiderbar
      breakpoint="md"
      // One trail per page: `Breadcrumbs` below. The layout's own would add a
      // second one wherever a menu path sits under another (Scheduled actions
      // under Instances, Parameter groups under Databases).
      breadcrumbRender={false}
      // One writer of the browser-tab title: the effect above. The layout's
      // own wrote the bare menu name ("Auto scaling groups") over it whenever
      // the menu was rebuilt — which is every page whose menu entry waits on
      // a capability — and titled the SASE pages by a rule of its own (B22).
      pageTitleRender={false}
      collapsed={collapsed}
      onCollapse={setCollapsed}
      location={{ pathname: location.pathname }}
      route={menuRoutes}
      // UI-02: opening a page leaves the groups the customer opened as they
      // are. The layout's default folds every other group on each navigation,
      // which moved the rest of the menu by hundreds of pixels under the cursor.
      menu={{ autoClose: false }}
      menuProps={{ selectedKeys: selectedKey ? [selectedKey] : [] }}
      avatarProps={avatarProps}
      actionsRender={() => [
        // AWS-style "estimated charges" always visible in the header (not buried
        // one click deep in the account menu). Click drills into billing.
        // On a phone-width header it overlapped the logo, and the account menu
        // carries the same figure — so it folds away there.
        ...(isNarrow
          ? []
          : saseEdition
            ? [<ActivityPanel key="activity" />]
            : [
              <Tooltip key="spend" title="Month-to-date spend — view billing">
                <Button
                  type="text"
                  aria-label="Month-to-date spend"
                  data-tour="topbar-spend"
                  onClick={() => navigate("/billing")}
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 600,
                  }}
                >
                  {monthSpend != null
                    ? formatMoney(monthSpend)
                    : "—"}
                </Button>
              </Tooltip>,
              <ActivityPanel key="activity" />,
            ]),
        <Tooltip key="search" title="Search resources (⌘/Ctrl+K)">
          <Button
            type="text"
            aria-label="Search"
            data-tour="topbar-search"
            icon={<SearchOutlined />}
            onClick={() => setSearchOpen(true)}
          />
        </Tooltip>,
        ...(routeTour && !isNarrow
          ? [
              <Tooltip key="tour" title={`Take the ${routeTour.name} tour`}>
                <Button
                  type="text"
                  aria-label="Take a tour of this page"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setTourRequestedAt(Date.now())}
                />
              </Tooltip>,
            ]
          : []),
        <Tooltip
          key="theme"
          title={themeMode === "dark" ? "Switch to light" : "Switch to dark"}
        >
          <Button
            type="text"
            aria-label="Toggle theme"
            icon={themeMode === "dark" ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
          />
        </Tooltip>,
      ]}
      menuItemRender={(item, dom) =>
        item.path ? <Link to={item.path}>{dom}</Link> : dom
      }
    >
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        scope={searchScope}
      />
      <ProductTour
        requestedAt={tourRequestedAt}
        onClose={() => setTourRequestedAt(null)}
      />
      <OperationsTracker />
      <DiscardGuard />
      {saseEdition ? null : <AccountBanner />}
      <ActingForBanner />
      <Breadcrumbs home={!(saseEdition || (saseAvailable && productView === "sase_only"))} />
      <EditionGate edition={saseEdition}>
        <Outlet />
      </EditionGate>
    </ProLayout>
  );
}

export default DashboardLayout;
